import { createContext, ReactNode, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from 'react-query'
import * as Sentry from '@sentry/react'
import { saveLocale, stringToBool } from '../../helpers/utils'
import { useLocalStorage } from '../../hooks/use-localstorage'
import { getMe } from '../../http'
import { getUserLanguage } from '../../Http/general'
import { Queries } from '../../queries'
import { UserStore } from '../../store/User/UserStore'
import { CenteredSpinner } from '../Common/Spinner'
interface IMainLayoutContext {
  user: UserStore
  sidebar: {
    hidden: boolean
    setHidden: (...args: any[]) => any
    toggle: () => any
  }
  header: {
    title?: string
    updateTitle: (node: ReactNode) => any
    clear: () => any
  }
  permission: number
  sidebarReady: boolean
  featureFlagValue: bigint
}

export const MainLayoutContext = createContext<IMainLayoutContext>(undefined)

export const MainLayoutContextProvider = ({ children }) => {
  const [sidebarHidden, setSidebarHidden] = useState(false)
  const [sidebarReady, setSidebarReady] = useState(false)
  const [headerTitle, setHeaderTitle] = useState()
  const { i18n } = useTranslation()
  const query = useQuery(Queries.ME, getMe, { enabled: !location.href.includes('public') })
  const { data: language } = useQuery([Queries.USER_LANGUAGE], () => getUserLanguage(), { enabled: !!query?.data })
  const [collapsed, setCollapsed] = useLocalStorage('sidebar-collapsed', true, stringToBool)

  useEffect(() => {
    setSidebarHidden(collapsed)
    setSidebarReady(true)
  }, [])

  const providerValue: IMainLayoutContext = useMemo(
    () => ({
      user: query.data ? new UserStore(query.data) : null,
      header: {
        title: headerTitle,
        updateTitle: setHeaderTitle,
        clear: () => setHeaderTitle(null),
      },
      sidebar: {
        hidden: sidebarHidden,
        setHidden: (val: boolean) => {
          setSidebarHidden(val)
          setCollapsed(val)
        },
        toggle: () => {
          setSidebarHidden(prev => {
            const newVal = !prev
            setCollapsed(newVal)
            return newVal
          })
        },
      },
      permission: query.data?.permissionValue,
      sidebarReady: sidebarReady,
      featureFlagValue: query.data?.featureFlagValue,
    }),
    [query.data, headerTitle, sidebarHidden, sidebarReady, setCollapsed],
  )

  useEffect(() => {
    if (language) {
      saveLocale(language)
      i18n.changeLanguage(language)
    }
  }, [language])
  useEffect(() => {
    if ('FreshworksWidget' in window && providerValue.user) {
      const { firstName, lastName, email } = providerValue.user.data
      window.FreshworksWidget('identify', 'ticketForm', {
        name: `${firstName} ${lastName}`,
        email,
      })
      window.FreshworksWidget('disable', 'ticketForm', ['name', 'email'])
    }
    if (providerValue.user) {
      const { firstName, lastName, email, userId } = providerValue.user.data
      Sentry.setUser({ email: email, username: `${firstName} ${lastName}`, id: userId })
    }
  }, [providerValue])

  return (
    <MainLayoutContext.Provider value={providerValue}>
      {query.isLoading ? <CenteredSpinner /> : providerValue && children}
    </MainLayoutContext.Provider>
  )
}
